/**
 * User Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { userService } from '../services/userService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  CreateUserPayload,
  UpdateUserPayload,
  GetUsersParams,
} from '../types/user.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get users list hook
 */
export function useGetUsers(
  params?: GetUsersParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.user,
      params?.page || 1,
      params?.limit || 10,
      params?.search || '',
      params?.isBlocked,
      params?.startDate,
      params?.endDate,
    ],
    queryFn: () => userService.getUsers(params),
    ...options,
  });
}

/**
 * Get single user by ID hook
 */
export function useGetUser(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.user, id],
    queryFn: () => userService.getUser(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create user hook
 */
export function useCreateUser(
  options?: UseMutationOptions<any, any, CreateUserPayload, any>
) {
  return useApiMutation<any, any, CreateUserPayload, any>({
    mutationFn: ((data: CreateUserPayload) => userService.createUser(data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.user] });
    },
    ...options,
  });
}

/**
 * Update user hook
 */
export function useUpdateUser(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateUserPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateUserPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateUserPayload }) =>
      userService.updateUser(variables.id, variables.data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.user] });
    },
    ...options,
  });
}

/**
 * Delete user hook
 */
export function useDeleteUser(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => userService.deleteUser(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.user] });
    },
    ...options,
  });
}

/**
 * Export users hook
 */
export function useExportUsers(
  options?: UseMutationOptions<any, any, GetUsersParams | undefined, any>
) {
  return useApiMutation<any, any, GetUsersParams | undefined, any>({
    mutationFn: ((params?: GetUsersParams) => userService.exportUsers(params)) as any,
    ...options,
  });
}

/**
 * Get community activity for a user hook
 */
export function useGetCommunityActivity(
  id: string,
  search?: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.user, 'community-activity', id, search || ''],
    queryFn: () => userService.getCommunityActivity(id, search),
    enabled: !!id,
    ...options,
  });
}

/**
 * Get achievements for a user hook
 */
export function useGetAchievements(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.user, 'achievements', id],
    queryFn: () => userService.getAchievements(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Get transaction history for a user hook
 */
export function useGetTransactions(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.user, 'transactions', id],
    queryFn: () => userService.getTransactions(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Get login history for a user hook
 */
export function useGetLoginHistory(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.user, 'login-history', id],
    queryFn: () => userService.getLoginHistory(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Get user activity hook
 */
export function useGetUserActivity(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.user, 'activity', id],
    queryFn: () => userService.getUserActivity(id),
    enabled: !!id,
    ...options,
  });
}

